/**
 * Text renderer — world-anchored, not billboarded, letters that fly in and land.
 *
 * The caller supplies the text block's plane as two screen-space basis vectors,
 * one per em of local x and y. Those come from projecting the anchor's 3D plane,
 * so foreshortening is real: text on a wall to your left is genuinely squashed,
 * and walking past it swings it. A billboarded quad cannot do that, and
 * billboarding is what makes AR text read as a sticker.
 *
 * Each glyph is a textured quad sampling one cell of the shared atlas built in
 * glyphs.ts — a real, antialiased letterform, not a scattered handful of points.
 * Assembly is a shader function of the per-vertex `order` and `seed` attributes,
 * so a whole letter flies in as one rigid shape and lands in reading sequence,
 * with zero CPU cost per frame. Letters fall along the plane's own up vector,
 * which means text lying flat on a table assembles by sliding in across the
 * surface rather than dropping through it.
 */

import type { TextCloud } from './textCloud.js';
import { atlasImage, atlasVersion } from './glyphs.js';
import { applyPs1Upscale, ps1Dpr, PS1 } from '../postfx/ps1.js';

export interface TextDrawItem {
  id: string;
  cloud: TextCloud;
  /** Block origin (left edge, last baseline) in CSS px. */
  ox: number;
  oy: number;
  /** Screen-space vector for one em of local +x, in CSS px. */
  bx: [number, number];
  /** Screen-space vector for one em of local +y, in CSS px. */
  by: [number, number];
  /** 0 scattered .. 1 fully assembled. */
  assembly: number;
  /** 0..1 overall. Callers fade this for distance and grazing angle. */
  alpha: number;
  colour: [number, number, number];
}

const VERT = `
precision mediump float;

attribute vec2 a_pos;    // em-local quad corner
attribute vec2 a_uv;
attribute float a_order;  // 0..1 reading order, shared across a glyph's 6 verts
attribute float a_seed;   // random seed, shared across a glyph's 6 verts

uniform vec2  u_origin;   // clip
uniform vec2  u_bx;       // clip per em
uniform vec2  u_by;
uniform float u_assembly;
uniform vec2  u_snap;

varying vec2 v_uv;
varying float v_alpha;

void main() {
  float r1 = fract(sin(a_seed * 78.233) * 43758.5453);
  float r2 = fract(sin(a_seed * 39.425 + 12.9898) * 24634.6345);

  // Reading-order stagger. Each glyph's own window is a slice of the whole
  // assembly, so the sentence writes itself left to right.
  float t = clamp((u_assembly - a_order * 0.55) / 0.45, 0.0, 1.0);
  // Ease-out with a slight overshoot settle — letters land rather than glide.
  float ease = 1.0 - pow(1.0 - t, 3.0);
  float settle = sin(t * 3.14159) * (1.0 - t) * 0.06;

  // Fall along the plane's own up vector, from above and slightly scattered.
  // The whole glyph moves together — a_pos varies per corner, the offset does
  // not — so a letter arrives as a rigid shape, not a dissolving smear.
  float drop = (1.0 - ease) * (1.4 + r1 * 1.8);
  vec2 offset = vec2((r2 - 0.5) * (1.0 - ease) * 0.5, drop + settle);
  vec2 local = a_pos + offset;

  vec2 clip = u_origin + u_bx * local.x + u_by * local.y;
  if (u_snap.x > 0.0) clip = floor(clip / u_snap + 0.5) * u_snap;

  gl_Position = vec4(clip, 0.0, 1.0);
  v_uv = a_uv;

  // Still-falling letters are fainter, so the leading edge of the sentence is
  // brighter than the words still on their way down.
  v_alpha = ease * ease;
}
`;

const FRAG = `
precision mediump float;
uniform sampler2D u_atlas;
uniform vec3 u_colour;
uniform float u_alpha;
varying vec2 v_uv;
varying float v_alpha;
void main() {
  float coverage = texture2D(u_atlas, v_uv).a;
  float a = coverage * u_alpha * v_alpha;
  if (a <= 0.004) discard;
  gl_FragColor = vec4(u_colour * a, a); // premultiplied
}
`;

interface Bufs {
  pos: WebGLBuffer;
  uv: WebGLBuffer;
  order: WebGLBuffer;
  seed: WebGLBuffer;
  count: number;
}

export class TextRenderer {
  private gl: WebGLRenderingContext | null = null;
  private prog: WebGLProgram | null = null;
  private loc: Record<string, WebGLUniformLocation | null> = {};
  private attr: Record<string, number> = {};
  private bufs = new Map<string, Bufs>();
  private texture: WebGLTexture | null = null;
  private uploadedAtlasVersion = -1;
  private w = 0;
  private h = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const gl =
      (canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: true,
        antialias: true,
      }) as WebGLRenderingContext | null) ??
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) return;
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    this.gl = gl;
    this.prog = prog;
    this.attr.a_pos = gl.getAttribLocation(prog, 'a_pos');
    this.attr.a_uv = gl.getAttribLocation(prog, 'a_uv');
    this.attr.a_order = gl.getAttribLocation(prog, 'a_order');
    this.attr.a_seed = gl.getAttribLocation(prog, 'a_seed');
    for (const n of ['u_origin', 'u_bx', 'u_by', 'u_assembly', 'u_snap', 'u_colour', 'u_alpha', 'u_atlas']) {
      this.loc[n] = gl.getUniformLocation(prog, n);
    }
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  get ready(): boolean {
    return this.gl !== null;
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    const d = ps1Dpr(dpr);
    this.w = cssW;
    this.h = cssH;
    this.canvas.width = Math.max(1, Math.round(cssW * d));
    this.canvas.height = Math.max(1, Math.round(cssH * d));
    applyPs1Upscale(this.canvas);
    this.gl?.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  /** Drop a cached buffer set — call when a fragment's text changes or it dies. */
  release(id: string): void {
    const gl = this.gl;
    const b = this.bufs.get(id);
    if (!gl || !b) return;
    gl.deleteBuffer(b.pos);
    gl.deleteBuffer(b.uv);
    gl.deleteBuffer(b.order);
    gl.deleteBuffer(b.seed);
    this.bufs.delete(id);
  }

  private buffersFor(id: string, cloud: TextCloud): Bufs | null {
    const gl = this.gl;
    if (!gl) return null;
    const hit = this.bufs.get(id);
    if (hit && hit.count === cloud.count) return hit;
    if (hit) this.release(id);

    const pos = gl.createBuffer();
    const uv = gl.createBuffer();
    const order = gl.createBuffer();
    const seed = gl.createBuffer();
    if (!pos || !uv || !order || !seed) return null;
    gl.bindBuffer(gl.ARRAY_BUFFER, pos);
    gl.bufferData(gl.ARRAY_BUFFER, cloud.pos.subarray(0, cloud.count * 2), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, uv);
    gl.bufferData(gl.ARRAY_BUFFER, cloud.uv.subarray(0, cloud.count * 2), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, order);
    gl.bufferData(gl.ARRAY_BUFFER, cloud.order.subarray(0, cloud.count), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, seed);
    gl.bufferData(gl.ARRAY_BUFFER, cloud.seed.subarray(0, cloud.count), gl.STATIC_DRAW);
    const b: Bufs = { pos, uv, order, seed, count: cloud.count };
    this.bufs.set(id, b);
    return b;
  }

  /** Re-upload the atlas texture if new glyphs were rasterised since last draw. */
  private syncAtlas(): void {
    const gl = this.gl;
    if (!gl || !this.texture) return;
    const v = atlasVersion();
    if (v === this.uploadedAtlasVersion) return;
    const img = atlasImage();
    if (!img) return;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img as TexImageSource);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    this.uploadedAtlasVersion = v;
  }

  clear(): void {
    const gl = this.gl;
    if (!gl) return;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  draw(items: TextDrawItem[]): void {
    const gl = this.gl;
    if (!gl || !this.prog) return;
    this.clear();
    this.syncAtlas();
    gl.useProgram(this.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(this.loc.u_atlas!, 0);

    for (const it of items) {
      if (it.alpha <= 0.004 || it.cloud.count === 0) continue;
      const b = this.buffersFor(it.id, it.cloud);
      if (!b) continue;

      gl.bindBuffer(gl.ARRAY_BUFFER, b.pos);
      gl.enableVertexAttribArray(this.attr.a_pos!);
      gl.vertexAttribPointer(this.attr.a_pos!, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.uv);
      gl.enableVertexAttribArray(this.attr.a_uv!);
      gl.vertexAttribPointer(this.attr.a_uv!, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.order);
      gl.enableVertexAttribArray(this.attr.a_order!);
      gl.vertexAttribPointer(this.attr.a_order!, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, b.seed);
      gl.enableVertexAttribArray(this.attr.a_seed!);
      gl.vertexAttribPointer(this.attr.a_seed!, 1, gl.FLOAT, false, 0, 0);

      // CSS px -> clip. Basis vectors are directions, so they scale but do not
      // translate: x by 2/w, y by -2/h.
      gl.uniform2f(this.loc.u_origin!, (it.ox / this.w) * 2 - 1, 1 - (it.oy / this.h) * 2);
      gl.uniform2f(this.loc.u_bx!, (it.bx[0] / this.w) * 2, -(it.bx[1] / this.h) * 2);
      gl.uniform2f(this.loc.u_by!, (it.by[0] / this.w) * 2, -(it.by[1] / this.h) * 2);
      gl.uniform1f(this.loc.u_assembly!, it.assembly);
      gl.uniform3f(this.loc.u_colour!, it.colour[0], it.colour[1], it.colour[2]);
      gl.uniform1f(this.loc.u_alpha!, it.alpha);
      const q = PS1.enabled ? (PS1.snapPx * 2) / this.canvas.width : 0;
      gl.uniform2f(this.loc.u_snap!, q, q);

      gl.drawArrays(gl.TRIANGLES, 0, b.count);
    }
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) return;
    for (const id of [...this.bufs.keys()]) this.release(id);
    if (this.texture) gl.deleteTexture(this.texture);
    this.texture = null;
    if (this.prog) gl.deleteProgram(this.prog);
    this.prog = null;
  }
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.warn('[TextRenderer] shader compile failed:', gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}
